import {
  BodyScrollEndEvent,
  ColDef,
  FirstDataRenderedEvent,
  RowClassParams,
  RowClickedEvent,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import GridTouch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
  cellDateComparator,
  onlyTouchGridStyle,
} from "src/utils/grid/gridFunctionUtil";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
  restoreGridScrollState,
  saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";

import {
  defaultKSK2130_R01HeaderDto,
  KSK2130_R01HeaderDto,
} from "./Dto_KSK2130_R01";
import { useKSK2130_R01Store } from "./Store_KSK2130_R01";

const COLUMN_STATE_KEY = "KSK2130_R01-order-grid-column-state-v1";
const HEADER_NAMES: Record<string, string> = {
  rowIndex: "",
  productOrderNo: "작업지시번호",
  orderDt: "작업일자",
  workplaceCd: "작업공정",
  workProcess: "공정유형",
  jobTeam: "작업팀",
  orderEmp: "작업자",
  orderEmpNm: "작업자명",
  itemCd: "품목코드",
  itemNm: "품명",
  itemSpec: "규격",
  itemUnit: "단위",
  orderQty: "수량",
  resultQty: "생산량",
};

const compareNumbers = (valueA: number | undefined, valueB: number | undefined) =>
  Number(valueA ?? 0) - Number(valueB ?? 0);

interface TableKSK2130_R01Props {
  gridRef: RefObject<AgGridReact<KSK2130_R01HeaderDto> | null>;
  fetchDetailList: (productOrder: KSK2130_R01HeaderDto) => Promise<boolean>;
  fetchLotList: (productOrder: KSK2130_R01HeaderDto) => Promise<void>;
}

export const Table_KSK2130_R01 = ({
  gridRef,
  fetchDetailList,
  fetchLotList,
}: TableKSK2130_R01Props) => {
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const rowClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const {
    KSK2130_R01List,
    selectedKSK2130_R01,
    initSelectedKSK2130_R01,
    columnState,
    setColumnState,
    scrollTop,
    setScrollTop,
  } = useKSK2130_R01Store();
  const { workProcessList, jobTeamList } = useCommonCodeStore();
  const { getRowId, createRowClassRules, paddedGridData } =
    useGridSettingContent<KSK2130_R01HeaderDto>({
      gridRef,
      key: "productOrderNo",
      gridData: KSK2130_R01List,
      defaultGridData: defaultKSK2130_R01HeaderDto,
      selectedRow: selectedKSK2130_R01,
    });
  const getCellStyle = useCallback((params: any, key: string) =>
    onlyTouchGridStyle(key, "productOrderNo")(params), []);

  const baseColumnDefs = useMemo<ColDef<KSK2130_R01HeaderDto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        headerName: "작업지시번호",
        colId: "productOrderNo",
        field: "productOrderNo",
        width: 150,
        cellStyle: (params) => getCellStyle(params, "productOrderNo"),
      },
      {
        headerName: "작업일자",
        colId: "orderDt",
        field: "orderDt",
        width: 110,
        valueFormatter: (params) =>
          params.value ? formatDate(params.value) : "",
        comparator: cellDateComparator,
        cellStyle: (params) => getCellStyle(params, "orderDt"),
      },
      {
        headerName: "작업공정",
        colId: "workplaceCd",
        field: "workplaceCd",
        width: 150,
        valueFormatter: (params) => params.data?.workplaceNm || params.value,
        cellStyle: (params) => getCellStyle(params, "workplaceCd"),
      },
      {
        headerName: "공정유형",
        colId: "workProcess",
        field: "workProcess",
        width: 90,
        cellStyle: (params) => getCellStyle(params, "workProcess"),
        valueFormatter: (params) =>
          workProcessList.find((item) => item.minorCd === params.value)
            ?.codeNm ?? params.value,
      },
      {
        headerName: "작업팀",
        colId: "jobTeam",
        field: "jobTeam",
        width: 75,
        cellStyle: (params) => getCellStyle(params, "jobTeam"),
        valueFormatter: (params) =>
          jobTeamList.find((item) => item.minorCd === params.value)?.codeNm ??
          params.value,
      },
      { headerName: "작업자", colId: "orderEmp", field: "orderEmp", width: 90,
        cellStyle: (params) => getCellStyle(params, "orderEmp") },
      { headerName: "작업자명", colId: "orderEmpNm", field: "orderEmpNm", width: 90,
        cellStyle: (params) => getCellStyle(params, "orderEmpNm") },
      { headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 130,
        cellStyle: (params) => getCellStyle(params, "itemCd") },
      { headerName: "품명", colId: "itemNm", field: "itemNm", width: 150,
        cellStyle: (params) => getCellStyle(params, "itemNm") },
      { headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 130,
        cellStyle: (params) => getCellStyle(params, "itemSpec") },
      { headerName: "단위", colId: "itemUnit", field: "itemUnit", width: 60,
        cellStyle: (params) => getCellStyle(params, "itemUnit") },
      {
        headerName: "수량",
        colId: "orderQty",
        field: "orderQty",
        width: 90,
        type: "numericColumn",
        cellStyle: (params) => getCellStyle(params, "orderQty"),
        valueFormatter: (params) =>
          Number(params.value ?? 0).toLocaleString(),
        comparator: compareNumbers,
      },
      {
        headerName: "생산량",
        colId: "resultQty",
        field: "resultQty",
        width: 90,
        type: "numericColumn",
        cellStyle: (params) => getCellStyle(params, "resultQty"),
        valueFormatter: (params) =>
          Number(params.value ?? 0).toLocaleString(),
        comparator: compareNumbers,
      },
    ],
    [getCellStyle, jobTeamList, workProcessList],
  );

  const columnDefs = useMemo<ColDef<KSK2130_R01HeaderDto>[]>(() =>
    GridCustomColumnDefs<KSK2130_R01HeaderDto>({
      baseColumnDefs,
      columnState: columnState?.some((state) => state.colId === "rowIndex")
        ? columnState
        : null,
      headerNames: HEADER_NAMES,
      fallbackHeaderClass: aggridHeaderClass,
    }) as ColDef<KSK2130_R01HeaderDto>[], [baseColumnDefs, columnState]);

  const pinnedBottomRowData = useMemo<KSK2130_R01HeaderDto[]>(
    () => [
      {
        ...defaultKSK2130_R01HeaderDto,
        productOrderNo: `${KSK2130_R01List.length.toLocaleString()}건`,
        orderDt: undefined,
        orderQty: KSK2130_R01List.reduce(
          (sum, item) => sum + Number(item.orderQty || 0),
          0,
        ),
        resultQty: KSK2130_R01List.reduce(
          (sum, item) => sum + Number(item.resultQty || 0),
          0,
        ),
      },
    ],
    [KSK2130_R01List],
  );

  const rowClassRules = useMemo<
    Record<
      string,
      (params: RowClassParams<KSK2130_R01HeaderDto>) => boolean
    >
  >(
    () => ({
      ...createRowClassRules<KSK2130_R01HeaderDto>({
        idKey: "productOrderNo",
        selectedList: selectedKSK2130_R01 ? [selectedKSK2130_R01] : [],
        deletedList: [],
      }),
      hover: (params) => params.node.rowIndex === hoveredRowIndex,
    }),
    [createRowClassRules, hoveredRowIndex, selectedKSK2130_R01],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<KSK2130_R01HeaderDto>) => {
      if (event.node.rowPinned || !event.data) return;
      if (rowClickTimerRef.current) clearTimeout(rowClickTimerRef.current);
      rowClickTimerRef.current = setTimeout(() => {
        rowClickTimerRef.current = null;
        void fetchDetailList(event.data!);
      }, 200);
    },
    [fetchDetailList],
  );

  const handleRowDoubleClick = useCallback(
    (event: RowDoubleClickedEvent<KSK2130_R01HeaderDto>) => {
      if (event.node.rowPinned || !event.data) return;
      if (rowClickTimerRef.current) {
        clearTimeout(rowClickTimerRef.current);
        rowClickTimerRef.current = null;
      }
      void fetchLotList(event.data);
    },
    [fetchLotList],
  );

  const handleSelectionClear = useCallback(() => {
    if (selectedKSK2130_R01) {
      void fetchDetailList(selectedKSK2130_R01);
      return;
    }
    initSelectedKSK2130_R01();
  }, [fetchDetailList, initSelectedKSK2130_R01, selectedKSK2130_R01]);

  const handleBodyScrollEnd = useCallback((
    event: BodyScrollEndEvent<KSK2130_R01HeaderDto>,
  ) => {
    if (event.direction === "vertical") {
      saveGridScrollState(event.api, setScrollTop);
    }
  }, [setScrollTop]);

  const handleFirstDataRendered = useCallback((
    event: FirstDataRenderedEvent<KSK2130_R01HeaderDto>,
  ) => {
    restoreGridScrollState(event.api, scrollTop);
  }, [scrollTop]);

  useEffect(
    () => () => {
      if (rowClickTimerRef.current) clearTimeout(rowClickTimerRef.current);
    },
    [],
  );

  return (
    <GridContextWrapper<KSK2130_R01HeaderDto>
      gridRef={gridRef}
      gridWrapperRef={gridWrapperRef}
      paddedGridData={paddedGridData}
      initSelected={handleSelectionClear}
      defaultRowData={defaultKSK2130_R01HeaderDto}
      baseColumnDefs={columnDefs}
      setColumnState={setColumnState}
      localStorageKey={COLUMN_STATE_KEY}
      enableDataHistory={false}
    >
      <GridTouch<KSK2130_R01HeaderDto>
        ref={gridRef}
        rowData={paddedGridData}
        getRowId={getRowId}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowClassRules={rowClassRules}
        onRowClicked={handleRowClick}
        onRowDoubleClicked={handleRowDoubleClick}
        onBodyScrollEnd={handleBodyScrollEnd}
        onFirstDataRendered={handleFirstDataRendered}
        pinnedBottomRowData={pinnedBottomRowData}
        setHoveredRowIndex={setHoveredRowIndex}
        columnStateKey={COLUMN_STATE_KEY}
        setColumnState={setColumnState}
      />
    </GridContextWrapper>
  );
};
